import React, { useCallback, useMemo, useContext, useState } from 'react';
import _ from 'lodash';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import { translate as t } from 'hooks/useTranslations';
import GridSection from 'components/_layouts/GridSection';
import { useTemplates } from '../../_shared/_data_sources/useTemplates';
import { Box, Typography, CircularProgress, Stack } from '@mui/material';
import PageToolbar from '_lex/_atoms/PageToolbar';
import PhxBadge from '_lex/_atoms/PhxBadge';
import { useNavigate } from 'react-router-dom';
import { AppContext } from 'views/App/context';
import { QuickSearch, ActionMenu } from 'components/_atoms';
import useUrlParams from 'hooks/useUrlParams';
import { TemplateStatus } from '../../_shared/_types';
import datetimeHelper from 'helpers/datetimeHelper';
import TemplateFiltersGroup from '../../_shared/_components/TemplateFiltersGroup';
import useAssignedModules from '../../_shared/_data_sources/useAssignedModules';
import { useConfirm } from 'material-ui-confirm';
import { useQueryClient } from 'react-query';
import { enqueueSnackbar } from 'notistack';
import useManageTemplates from '../../_shared/_data_sources/useManageTemplates';

export type TemplateListParams = {
  search?: string | null;
  status?: TemplateStatus | null;
  effectiveDateFrom?: string | null;
  effectiveDateTo?: string | null;
  lastUpdatedFrom?: string | null;
  lastUpdatedTo?: string | null;
  authors?: string[] | null;
};

const defaultParams: TemplateListParams = {
  search: null,
  status: null,
  effectiveDateFrom: null,
  effectiveDateTo: null,
  lastUpdatedFrom: null,
  lastUpdatedTo: null,
  authors: null,
};

const AgencyTemplateList: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission, currentOrganizationId } = useContext(AppContext);
  const [params, setParams] = useUrlParams(defaultParams);
  const assignedModulesQuery = useAssignedModules();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { deactivateTemplate } = useManageTemplates();
  const [deactivatingVersions, setDeactivatingVersions] = useState<Set<number>>(new Set());

  const updateParams = useCallback(
    (updatedParams: TemplateListParams) => {
      setParams((prev: TemplateListParams) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams]
  );

  const clearParams = useCallback(() => {
    setParams(defaultParams);
    document.body.dispatchEvent(new Event('params-changed'));
  }, [setParams]);

  const queryParams = useMemo(() => {
    const cleanParams = _.omitBy({ ...params }, _.isEmpty);
    return {
      ...cleanParams,
      ...(currentOrganizationId ? { organizationId: currentOrganizationId } : {}),
    };
  }, [params, currentOrganizationId]);

  const isFiltered = !_.isEmpty(queryParams);

  const templatesQuery = useTemplates(queryParams);
  const allTemplates = templatesQuery.data?.pages.flatMap((page) => page.content) || [];
  const totalCount = templatesQuery.data?.pages[0]?.count || 0;
  const { isLoading, isError, error } = templatesQuery;

  const getAssignedModuleName = useCallback(
    (moduleId: number) => {
      const module = assignedModulesQuery.data?.find((m) => m.id === moduleId);
      return module?.name || `Module ${moduleId}`;
    },
    [assignedModulesQuery.data]
  );

  const canManageTemplates = hasPermission('manageFieldBank', currentOrganizationId || '');

  const onSearchChange = useCallback(
    (search: string) => {
      updateParams({ search: search || null });
    },
    [updateParams]
  );

  const formatDate = (dateString?: string) => {
    return datetimeHelper.formatDate(dateString, { format: 'short', missingDisplay: '-' });
  };

  // For agency admins, combine id and versionId to create unique key
  // since same template id can have both Active and Draft versions
  const getRowId = useCallback(
    (row: any) => {
      return `${row.id}-${row.versionId}`;
    },
    []
  );

  const normalizeTemplateCondition = (condition: string | null): string => {
    if (!condition) return 'inSync';
    return condition.charAt(0).toLowerCase() + condition.slice(1);
  };

  const handleDeactivate = useCallback(
    async (templateId: number, versionId: number) => {
      try {
        await confirm({
          title: t('performanceReporting.templates.details.deactivateConfirm.title'),
          description: t('performanceReporting.templates.details.deactivateConfirm.message'),
          confirmationText: t('performanceReporting.templates.details.deactivateConfirm.confirm'),
          cancellationText: t('performanceReporting.templates.details.deactivateConfirm.cancel'),
          confirmationButtonProps: { color: 'error', variant: 'contained' },
          cancellationButtonProps: { color: 'neutral', variant: 'outlined' },
        });

        // Set loading state for this version
        setDeactivatingVersions((prev) => new Set(prev).add(versionId));

        // Call deactivation API
        await deactivateTemplate(
          templateId,
          versionId,
          () => {
            // Success callback
            enqueueSnackbar(t('performanceReporting.templates.details.actions.deactivateSuccess'), {
              variant: 'success',
            });
            queryClient.invalidateQueries(['templates']);
            setDeactivatingVersions((prev) => {
              const next = new Set([...prev]);
              next.delete(versionId);
              return next;
            });
          },
          (errors) => {
            enqueueSnackbar(t('performanceReporting.templates.details.actions.deactivateError'), {
              variant: 'error',
            });
            setDeactivatingVersions((prev) => {
              const next = new Set([...prev]);
              next.delete(versionId);
              return next;
            });
          }
        );
      } catch {
        // User cancelled - do nothing
      }
    },
    [confirm, deactivateTemplate, queryClient, t]
  );

  const getActionMenuItems = useCallback(
    (templateId: number, row: any) => [
      {
        key: 'details',
        label: t('performanceReporting.templates.list.actions.templateDetails'),
        onClick: () => {
          const isDraft = row.status === 'Draft';
          const url = isDraft ? `./details/${templateId}?isDraft=true` : `./details/${templateId}`;
          navigate(url);
        },
      },
      // Show View Template for Active templates
      ...(row.status === 'Active'
        ? [
            {
              key: 'view',
              label: t('performanceReporting.templates.list.actions.viewTemplate'),
              onClick: () => navigate(`./view/${templateId}`),
            },
          ]
        : []),
      // Show Deactivate for Active templates (with permission check)
      ...(canManageTemplates && row.status === 'Active'
        ? [
            {
              key: 'deactivate',
              label: t('performanceReporting.templates.details.actions.deactivate'),
              onClick: () => handleDeactivate(templateId, row.versionId),
              disabled: deactivatingVersions.has(row.versionId),
            },
          ]
        : []),
      // Only show edit for Draft templates
      ...(canManageTemplates && row.status === 'Draft'
        ? [
            {
              key: 'edit',
              label: t('performanceReporting.templates.list.actions.edit'),
              onClick: () => navigate(`./builder/${templateId}`),
            },
          ]
        : []),
    ],
    [canManageTemplates, navigate, handleDeactivate, deactivatingVersions, t]
  );

  const columns = useMemo(
    () => ({
      name: {
        header: t('performanceReporting.templates.list.columns.name'),
        flex: 2,
        renderer: (params: any) => (
          <Typography
            variant='body2'
            fontWeight='medium'
          >
            {params.row.name}
          </Typography>
        ),
      },
      module: {
        header: t('performanceReporting.templates.list.columns.module'),
        flex: 1.5,
        renderer: (params: any) => (
          <Typography variant='body2'>{getAssignedModuleName(params.row.assignedModuleId)}</Typography>
        ),
      },
      publishedDate: {
        header: t('performanceReporting.templates.list.columns.publishedDate'),
        flex: 1,
        renderer: (params: any) => (
          <Typography variant='body2'>{formatDate(params.row.lastPublishDate)}</Typography>
        ),
      },
      status: {
        header: t('performanceReporting.templates.list.columns.status'),
        flex: 1,
        renderer: (params: any) => {
          const status = params.row.status;
          const color = status === 'Active' ? 'success' : 'neutral';
          return (
            <PhxBadge
              color={color}
              size='sm'
            >
              {t(`performanceReporting.templates.list.status.${status.toUpperCase()}`)}
            </PhxBadge>
          );
        },
      },
      templateCondition: {
        header: t('performanceReporting.templates.list.columns.templateCondition'),
        flex: 1.5,
        renderer: (params: any) => {
          // Only show template condition for Draft status
          if (params.row.status !== 'Draft') {
            return <Typography variant='body2'>-</Typography>;
          }
          // Normalize backend value to match translation keys
          const condition = normalizeTemplateCondition(params.row.templateCondition);
          return (
            <PhxBadge
              color='default'
              size='sm'
            >
              {t(`performanceReporting.templates.list.templateCondition.${condition}`)}
            </PhxBadge>
          );
        },
      },
      actions: {
        header: '',
        flex: 0.5,
        renderer: (params: any) =>
          deactivatingVersions.has(params.row.versionId) ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <ActionMenu
              id={`template-actions-${params.row.id}-${params.row.versionId}`}
              items={getActionMenuItems(params.row.id, params.row)}
            />
          ),
      },
    }),
    [getAssignedModuleName, getActionMenuItems, formatDate, deactivatingVersions]
  );

  return (
    <CommonLayout
      pageTitle={t('performanceReporting.templates.list.title')}
      headerProps={{
        title: t('performanceReporting.templates.list.title'),
        subtitle: t('performanceReporting.templates.agency.list.subtitle'),
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto auto 1fr', gap: 4, minHeight: 0 }}>
        <PageToolbar
          leftItems={
            <Stack
              direction='row'
              spacing={3}
              sx={{
                alignItems: 'center',
                '& > *': {
                  minWidth: 'fit-content',
                  flexShrink: 0,
                },
              }}
            >
              <QuickSearch
                id='templates_search'
                initialValue={params.search || ''}
                onChange={onSearchChange}
                placeholder={t('performanceReporting.templates.list.search.placeholder')}
              />
            </Stack>
          }
          rightItems={null}
        />
        <TemplateFiltersGroup
          params={params}
          updateParams={updateParams}
          clearParams={clearParams}
        />
        {isLoading ? (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <GridSection
            columns={columns}
            data={allTemplates}
            totalCount={totalCount}
            query={templatesQuery}
            gridKey={JSON.stringify(queryParams)}
            gridProps={{
              getRowId,
            }}
          />
        )}
      </Box>
    </CommonLayout>
  );
};

export default AgencyTemplateList;

